import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Buffer } from 'buffer';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseResponse } from '../models/base.response';
import { FileModel } from '../models/file.model';
import { SubmittedPhoto } from '../models/submitted-photo.model';
const endpoints = {
  upload: (flightCode: string) => '/file/upload?flightCode=' + flightCode,
  uploadBatch: '/file/batch-upload',
  getSubmittedPhotos: '/train-image'
};

@Injectable()
export class FileService {

  constructor(
    private http: HttpClient
  ) { }

  upload(file: File, flightCode?: string): Observable<BaseResponse<FileModel>> {
    const url = environment.apiBaseUrl + endpoints.upload(flightCode);
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<BaseResponse<FileModel>>(url, formData);
  }

  uploadBatch(files: File[], params: { flightCode?: string, productCode?: string, type?: string, description?: string }) {
    const url = environment.apiBaseUrl + endpoints.uploadBatch;
    const formData = new FormData();
    console.log(files);
    formData.append("flightCode", params.flightCode || "");
    formData.append("productCode", params.productCode || "");
    formData.append("type", params.type || "");
    formData.append("description", params.description || "");
    for (const file of files) {
      formData.append(new Date().getTime() + '_' + file.name, file);
    }
    return this.http.post<BaseResponse<any>>(url, formData);
  }

  dataUrlToFile(dataURL: string, fileName: string): File | undefined {
    const arr = dataURL.split(',');
    if (arr.length < 2) { return undefined; }
    const mimeArr = arr[0].match(/:(.*?);/);
    if (!mimeArr || mimeArr.length < 2) { return undefined; }
    const mime = mimeArr[1];
    const buff = Buffer.from(arr[1], 'base64');
    return new File([buff], fileName, { type: mime });
  }

  getMimeType(fileName: string): string {
    const extension = this.getFileExtension(fileName);
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'pdf':
        return 'application/pdf';
      case 'doc':
      case 'docx':
        return 'application/msword';
      case 'ppt':
      case 'pptx':
        return 'application/vnd.ms-powerpoint';
      case 'xls':
      case 'xlsx':
        return 'application/vnd.ms-excel';
      case 'mp3':
        return 'audio/mpeg';
      case 'wav':
        return 'audio/wav';
      case 'mp4':
        return 'video/mp4';
      case 'mov':
        return 'video/quicktime';
      default:
        return 'application/octet-stream';
    }
  }

  getFileSize(size: number): string {
    const sizeInMb = (size / 1024 / 1024);
    return sizeInMb < 1 ? Math.floor((sizeInMb * 1024)) + ' kB' : sizeInMb.toFixed(1).replace('.0', '') + ' MB';
  }

  getFileText(file: File | FileModel): string {
    if (!file) {
      return '';
    }
    return (file instanceof File ? file.name : file.fileName) + ' (' + this.getFileSize(file.size) + ')';
  }

  getFileExtension(name: string): string {
    return name.split('.').pop().toUpperCase();
  }

  isPDF(file: FileModel): boolean {
    return this.getFileExtension(file.fileName) === 'PDF';
  }

  isImage(file: FileModel | File): boolean {
    const name = file instanceof File ? file.name : file.fileName;
    return ['JPEG', 'JPG', 'PNG'].indexOf(this.getFileExtension(name)) > -1;
  }

  getFileIcon(file: FileModel | File): string {
    const name = file instanceof File ? file.name : file.fileName;
    const ext = this.getFileExtension(name).toLowerCase();
    if (['xlsx', 'xls', 'csv'].indexOf(ext) > -1) {
      return 'bi bi-file-earmark-spreadsheet-fill text-gray-700';
    }
    if (['jpg', 'jpeg', 'png'].indexOf(ext) > -1) {
      return 'bi bi-file-earmark-image-fill text-gray-700';
    }
    if (['ppt', 'pptx'].indexOf(ext) > -1) {
      return 'bi bi-file-earmark-ppt-fill text-gray-700';
    }
    if (['pdf', 'txt', 'doc', 'docx'].indexOf(ext) > -1) {
      return 'bi bi-file-earmark-text-fill text-gray-700';
    }
    return 'bi bi-file-earmark-fill text-gray-700';
  }

  getSubmittedPhotos(): Observable<BaseResponse<SubmittedPhoto[]>> {
    const url = environment.apiBaseUrl + endpoints.getSubmittedPhotos;
    return this.http.get<BaseResponse<SubmittedPhoto[]>>(url);
  }

}
